import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { map, first, catchError, takeUntil } from 'rxjs/operators';
import { environment as env } from '../../../environments/environment';
import { LgvUserService } from './lgv-user.service';
import { LgvModel } from '../../data-models/longevity.model';

export interface LocationInfo {
  county: string;
  stateCode: string;
  postalCode: string;
  [key: string]: any;
}

export interface LocationState {
  postalCode?: string;
  county?: string;
  stateCode?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LocationService {
  private locationState$ = new BehaviorSubject<LocationState>({});

  constructor(
    private http: HttpClient,
    private lgvUserSvc: LgvUserService
  ) {}

  /**
   * Get the current location state as an Observable
   */
  getLocationState(): Observable<LocationState> {
    return this.locationState$.asObservable();
  }

  /**
   * Update the current location state
   */
  updateLocationState(state: LocationState): void {
    this.locationState$.next(state);
  }

  /**
   * Get the current logged-in user ID
   */
  private getCurrentUserId(): string | undefined {
    return this.lgvUserSvc?.DBUserSS$?.value?.agentInfo?.id;
  }

  /**
   * Look up location information from a zip code
   * Includes filtering by licensed states if userId is provided
   * @param zipCode The zip code to look up
   * @param userId Optional user ID to filter by licensed states
   */
  lookupLocationByZip(zipCode: string, userId?: string): Observable<LocationInfo | null> {
    // If no userId provided, use the current user's ID
    if (!userId && !this.lgvUserSvc.IsProcessor && !this.lgvUserSvc.IsAdmin) {
      userId = this.getCurrentUserId();
    }

    const bldUrl = `${env.LgvApiBaseUrl}/public/locationDetails?$filter=postalCode eq '${zipCode}'${userId ? `&userId=${userId}` : ''}`;

    return this.http.get<any>(bldUrl).pipe(
      first(),
      map(response => {
        if (response?.value && response.value.length > 0) {
          const locationInfo = response.value[0] as LocationInfo;
          
          // Update the location state
          this.updateLocationState({
            postalCode: zipCode,
            county: locationInfo.county,
            stateCode: locationInfo.stateCode
          });
          
          return locationInfo;
        }
        return null;
      }),
      catchError(error => {
        console.error('Error looking up location information:', error);
        return of(null);
      })
    );
  }

  /**
   * Check if a state is licensed for the current user
   * @param stateCode The state code to check
   * @param userId Optional user ID to check against (defaults to current user)
   */
  isStateLicensed(stateCode: string, userId?: string): Observable<boolean> {
    if (!userId) {
      userId = this.getCurrentUserId();
    }

    if (!userId) {
      return of(true); // No user ID means no filtering
    }

    // We can use the locationDetails endpoint with a state filter to see if any results come back
    const bldUrl = `${env.LgvApiBaseUrl}/public/locationDetails?$filter=stateCode eq '${stateCode}'&userId=${userId}&$top=1`;

    return this.http.get<any>(bldUrl).pipe(
      first(),
      map(response => {
        return !!(response?.value && response.value.length > 0);
      }),
      catchError(error => {
        console.error('Error checking if state is licensed:', error);
        return of(false);
      })
    );
  }

  /**
   * Get licensed states for a user
   * @param userId Optional user ID (defaults to current user)
   */
  getLicensedStates(userId?: string): Observable<string[]> {
    if (!userId) {
      userId = this.getCurrentUserId();
    }

    if (!userId) {
      return of([]); // No user ID means no filtering
    }

    // Use the distinct query to get only unique state codes
    const bldUrl = `${env.LgvApiBaseUrl}/public/locationDetails?userId=${userId}&$select=stateCode&$orderby=stateCode`;

    return this.http.get<any>(bldUrl).pipe(
      first(),
      map(response => {
        if (response?.value && response.value.length > 0) {
          // Extract and deduplicate state codes
          const states = [...new Set(response.value.map((item: any) => item.stateCode))] as string[];
          return states;
        }
        return [] as string[];
      }),
      catchError(error => {
        console.error('Error getting licensed states:', error);
        return of([] as string[]);
      })
    );
  }

  geoLocationLookup(): Observable<LgvModel.locationInfo> {
    const bldUrl = `${env.LgvApiBaseUrl}/public/geolocation`;

    return this.http
      .get<LgvModel.locationInfo>(bldUrl)
      .pipe(
        first(),
        catchError(err => {
          console.log('Error looking up geocode info', err);
          return of(null);
        }),
        map(locationInfo => {
          return locationInfo;
        })
      );
  }
} 